/**
 * FindingFormDialog -- Focused dialog form for creating or editing a finding
 *
 * Features:
 * - Left accent border color reacts to selected severity
 * - Severity as visual chip buttons (Critical / Major / Minor / Observation)
 * - Auto-focus on title field when opened
 * - Spring animation entrance/exit
 * - Resets fields on open using initialValues
 */

import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import type { SeverityType } from "@/packages/domains/findings";
import {
  SEVERITY_COLORS,
  useSeverityOptions,
} from "../../findings/findings-constants";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FindingFormValues {
  title: string;
  severity: SeverityType;
  observation: string;
  recommendation: string;
}

interface FindingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: Partial<FindingFormValues>;
  onSubmit: (values: FindingFormValues) => Promise<void>;
  isPending: boolean;
  mode: "create" | "edit";
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FindingFormDialog({
  open,
  onOpenChange,
  initialValues,
  onSubmit,
  isPending,
  mode,
}: FindingFormDialogProps) {
  const { t } = useTranslation("audits");
  const severityOptions = useSeverityOptions();

  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [severity, setSeverity] = useState<SeverityType>(
    initialValues?.severity ?? "major",
  );
  const [observation, setObservation] = useState(
    initialValues?.observation ?? "",
  );
  const [recommendation, setRecommendation] = useState(
    initialValues?.recommendation ?? "",
  );

  // Reset form fields whenever the dialog opens
  useEffect(() => {
    if (open) {
      setTitle(initialValues?.title ?? "");
      setSeverity(initialValues?.severity ?? "major");
      setObservation(initialValues?.observation ?? "");
      setRecommendation(initialValues?.recommendation ?? "");
    }
  }, [
    open,
    initialValues?.title,
    initialValues?.severity,
    initialValues?.observation,
    initialValues?.recommendation,
  ]);

  const accentColor = SEVERITY_COLORS[severity].border;
  const isValid = title.trim().length > 0 && observation.trim().length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || isPending) return;
    await onSubmit({
      title: title.trim(),
      severity,
      observation: observation.trim(),
      recommendation: recommendation.trim(),
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[1px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) onOpenChange(false);
                }}
              >
                <motion.div
                  className="w-full max-w-[460px] rounded-xl bg-card shadow-2xl overflow-hidden focus:outline-none"
                  style={{ borderLeft: `3px solid ${accentColor}` }}
                  onClick={(e) => e.stopPropagation()}
                  initial={{ y: 16, opacity: 0, scale: 0.97 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: 16, opacity: 0, scale: 0.97 }}
                  transition={{ type: "spring", damping: 28, stiffness: 360 }}
                >
                  <form onSubmit={handleSubmit}>
                    {/* ── Header ── */}
                    <div className="px-5 pt-5 pb-4 border-b border-border">
                      <div className="flex items-start justify-between mb-3.5">
                        <Dialog.Title className="text-[13px] font-semibold text-primary leading-tight">
                          {mode === "create"
                            ? t("findings.form.newFinding")
                            : t("findings.form.editFinding")}
                        </Dialog.Title>
                        <Dialog.Close asChild>
                          <button
                            type="button"
                            className="p-1 -mt-0.5 -mr-0.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted/40 transition-colors"
                            aria-label={t("checklist.header.close")}
                          >
                            <X size={14} />
                          </button>
                        </Dialog.Close>
                      </div>

                      {/* Severity chips */}
                      <div className="flex gap-1.5 flex-wrap">
                        {severityOptions.map((opt) => {
                          const config = SEVERITY_COLORS[opt.value];
                          const isSelected = severity === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setSeverity(opt.value)}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all"
                              style={
                                isSelected
                                  ? {
                                      backgroundColor: config.bg,
                                      borderColor: config.border,
                                      color: config.dot,
                                    }
                                  : {
                                      backgroundColor: "transparent",
                                      borderColor: "transparent",
                                      color: "var(--color-muted-foreground)",
                                      opacity: 0.6,
                                    }
                              }
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: config.dot }}
                              />
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Body ── */}
                    <div className="px-5 py-4 space-y-3.5">
                      {/* Title */}
                      <div>
                        <label
                          htmlFor="finding-title"
                          className="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5"
                        >
                          {t("findings.form.titleLabel")}{" "}
                          <span className="text-destructive">*</span>
                        </label>
                        <input
                          id="finding-title"
                          className="w-full bg-background border border-input rounded-md px-3 py-1.5 text-sm text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring transition-shadow"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder={t("findings.placeholders.title")}
                        />
                      </div>

                      {/* Observation */}
                      <div>
                        <label
                          htmlFor="finding-observation"
                          className="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5"
                        >
                          {t("findings.form.observationLabel")}{" "}
                          <span className="text-destructive">*</span>
                        </label>
                        <textarea
                          id="finding-observation"
                          className="w-full bg-background border border-input rounded-md px-3 py-2 text-xs text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring resize-none min-h-[72px] transition-shadow"
                          value={observation}
                          onChange={(e) => setObservation(e.target.value)}
                          placeholder={t("findings.placeholders.observation")}
                        />
                      </div>

                      {/* Recommendation */}
                      <div>
                        <label
                          htmlFor="finding-recommendation"
                          className="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5"
                        >
                          {t("findings.form.recommendationLabel")}{" "}
                          <span className="text-[9px] font-normal text-muted-foreground/50 normal-case tracking-normal">
                            (optional)
                          </span>
                        </label>
                        <textarea
                          id="finding-recommendation"
                          className="w-full bg-background border border-input rounded-md px-3 py-2 text-xs text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring resize-none min-h-[64px] transition-shadow"
                          value={recommendation}
                          onChange={(e) => setRecommendation(e.target.value)}
                          placeholder={t(
                            "findings.placeholders.recommendation",
                          )}
                        />
                      </div>
                    </div>

                    {/* ── Footer ── */}
                    <div className="flex items-center justify-end gap-2 px-5 pb-5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenChange(false)}
                      >
                        {t("findings.form.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!isValid || isPending}
                        loading={isPending}
                      >
                        {mode === "create"
                          ? t("findings.form.saveFinding")
                          : t("findings.form.save")}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
